import gitInfo from './gitInfo'

interface ServerConfig {
  restLandingURL: string
  restURL: string
  contactURL: string
  logoApiURL: string
  officeNextURL: string
  URL: string
  JMAP_URL: string
  BASE_URL: string
  davURL?: string // CalDAV/CardDAV 호스트. 미지정 시 BASE_URL 사용 (jmap/dav 호스트 분리 환경 전용)
  mailboxApiURL: string
  storageApiURL: string
  scheduleApiURL: string
  secureSendApiURL: string
  readReceiptApiURL: string
  aiApiURL: string
  openSearchApiURL: string
  wsURL: string | null
  authURL: string
  orgChartURL?: string
  maintenanceURL: string
}

interface ConfProp {
  DEBUG: boolean
  SERVER: ServerConfig
  officeNextURL: string
  defaultLocale?: string
  gitInfo?: any
}

// window.globalConfig 타입 확장
declare global {
  interface Window {
    globalConfig?: {
      JMAP_URL?: string
      wsURL?: string
      LOCALE?: string
    }
  }

  // Navigator 타입 확장 (IE의 userLanguage 지원)
  interface Navigator {
    userLanguage?: string
  }
}

// ✅ 불린 상수를 사용하여 확실한 tree-shaking 보장
const getServerConfig = (): { server: ServerConfig; officeNextURL: string } => {
  if (__ENV_PRODUCTION__) {
    return {
      server: {
        restLandingURL: 'https://api.officemail.io/v1',
        restURL: 'https://api.officemail.io/v1',
        contactURL: 'https://crm-contacts.officemail.io',
        logoApiURL: 'https://api.logo.bi',
        officeNextURL: 'https://api.officenext.net',
        URL: 'https://api.officemail.io/graphql',
        JMAP_URL: 'https://mail.officemail.io/jmap',
        BASE_URL: 'https://mail.officemail.io',
        mailboxApiURL: 'https://mailbox-api.officemail.io/api/v1',
        storageApiURL: 'https://storage.officemail.io/api/v1',
        scheduleApiURL: 'https://schedule-send.officemail.io',
        secureSendApiURL: 'https://secure-send.officemail.io',
        readReceiptApiURL: 'https://read-receipt.officemail.io',
        aiApiURL: 'https://api.officemail.ai',
        // 운영 OpenSearch 주소 (OFM-212 확정, 현재 값으로 정상 동작 확인).
        openSearchApiURL: 'https://search.officemail.io/api/v1',
        wsURL: null,
        authURL: 'https://auth.officemail.io/api/v1',
        orgChartURL: 'https://orgchart.officemail.io/v1',
        maintenanceURL: 'https://maintenance.officemail.io/notice.json',
      },
      officeNextURL: 'https://org.officenext.net',
    }
  }

  if (__ENV_STAGING__) {
    // staging은 production과 동일한 api서버 사용.
    return {
      server: {
        restLandingURL: 'https://api.officemail.io/v1',
        restURL: 'https://api.officemail.io/v1',
        contactURL: 'https://crm-contacts.officemail.io',
        logoApiURL: 'https://api.logo.bi',
        officeNextURL: 'https://api.officenext.net',
        URL: 'https://api.officemail.io/graphql',
        JMAP_URL: 'https://mail.officemail.io/jmap',
        BASE_URL: 'https://mail.officemail.io',
        mailboxApiURL: 'https://mailbox-api.officemail.io/api/v1',
        storageApiURL: 'https://storage.officemail.io/api/v1',
        scheduleApiURL: 'https://schedule-send.officemail.io',
        secureSendApiURL: 'https://secure-send.officemail.io',
        readReceiptApiURL: 'https://read-receipt.officemail.io',
        aiApiURL: 'https://api.officemail.ai',
        // staging은 production과 동일 서버 사용. 운영 OpenSearch 주소 OFM-212 확정.
        openSearchApiURL: 'https://search.officemail.io/api/v1',
        wsURL: null,
        authURL: 'https://auth.officemail.io/api/v1',
        orgChartURL: 'https://orgchart.officemail.io/v1',
        maintenanceURL: 'https://maintenance.officemail.io/staging/notice.json',
      },
      officeNextURL: 'https://org.officenext.net',
    }
  }

  if (__ENV_PROXY_DEV__) {
    return {
      server: {
        restLandingURL: 'https://localhost:3151/v1',
        restURL: 'https://localhost:3152/v1',
        contactURL: 'https://localhost:3153',
        logoApiURL: 'https://localhost:3156',
        officeNextURL: 'https://localhost:3157',
        URL: 'https://localhost:3158/graphql',
        JMAP_URL: 'https://localhost:3159/jmap',
        BASE_URL: 'https://localhost:3160',
        mailboxApiURL: 'https://localhost:3161/api/v1',
        storageApiURL: 'https://localhost:3162/api/v1',
        scheduleApiURL: 'https://localhost:3163',
        secureSendApiURL: 'https://localhost:3169',
        readReceiptApiURL: 'https://localhost:3170',
        aiApiURL: 'https://localhost:3164',
        openSearchApiURL: 'https://localhost:3165/api/v1',
        wsURL: null,
        authURL: 'https://localhost:3166/api/v1',
        orgChartURL: 'https://localhost:3167/v1',
        maintenanceURL: 'https://maintenance.officemail.io/dev/notice.json',
      },
      officeNextURL: 'https://localhost:3168',
    }
  }

  if (__ENV_PROXY_DEV2__) {
    // dev 서버를 로컬 proxy2(3300 기반)로 경유해 접근한다. 타겟은 __ENV_PROXY_DEV__ 블록과 동일한
    // dev 서버이며, 포트만 3300 기반으로 미러링(3151→3301)했다. dev proxy(3150)와 동시에 두 번째
    // 인스턴스를 띄울 때(다른 worktree가 3150 점유 중 등) 포트 충돌 없이 사용한다.
    // proxy_server/proxy2-config.mjs 에서 각 포트를 dev URL로 포워딩한다.
    return {
      server: {
        restLandingURL: 'https://localhost:3301/v1',
        restURL: 'https://localhost:3302/v1',
        contactURL: 'https://localhost:3303',
        logoApiURL: 'https://localhost:3306',
        officeNextURL: 'https://localhost:3307',
        URL: 'https://localhost:3308/graphql',
        JMAP_URL: 'https://localhost:3309/jmap',
        BASE_URL: 'https://localhost:3310',
        mailboxApiURL: 'https://localhost:3311/api/v1',
        storageApiURL: 'https://localhost:3312/api/v1',
        scheduleApiURL: 'https://localhost:3313',
        secureSendApiURL: 'https://localhost:3319',
        readReceiptApiURL: 'https://localhost:3320',
        aiApiURL: 'https://localhost:3314',
        openSearchApiURL: 'https://localhost:3315/api/v1',
        wsURL: null,
        authURL: 'https://localhost:3316/api/v1',
        orgChartURL: 'https://localhost:3317/v1',
        maintenanceURL: 'https://maintenance.officemail.io/dev/notice.json',
      },
      officeNextURL: 'https://localhost:3318',
    }
  }

  if (__ENV_QC__) {
    // QC 전용 백엔드. qc용으로 명시되지 않은 항목은 develop 값 사용.
    return {
      server: {
        restLandingURL: 'https://qc-api.officemail.io/v1',
        restURL: 'https://qc-api.officemail.io/v1',
        contactURL: 'https://qc-crm-contacts.officemail.io',
        logoApiURL: 'https://api.logo.bi',
        officeNextURL: 'https://qc-api.officenext.net',
        URL: 'https://qc-api.officemail.io/graphql',
        JMAP_URL: 'https://jmap.officemail.io.kr/jmap',
        BASE_URL: 'https://jmap.officemail.io.kr',
        davURL: 'https://caldav.officemail.io.kr',
        mailboxApiURL: 'https://mailbox-api.officemail.io.kr/api/v1',
        storageApiURL: 'https://qc-storage.officemail.io/api/v1',
        scheduleApiURL: 'https://qc-schedule-send.officemail.io',
        secureSendApiURL: 'https://qc-secure-send.officemail.io',
        readReceiptApiURL: 'https://qc-read-receipt.officemail.io',
        aiApiURL: 'https://qc-api.officemail.ai',
        openSearchApiURL: 'https://search.officemail.io.kr/api/v1',
        wsURL: null,
        authURL: 'https://qc-auth.officemail.app/api/v1',
        orgChartURL: 'https://qc-orgchart.officemail.app/v1',
        maintenanceURL: 'https://maintenance.officemail.io/dev/notice.json',
      },
      officeNextURL: 'https://qc-org.officenext.net',
    }
  }

  // development, log, debug, local 모두 dev 서버 사용
  return {
    server: {
      restLandingURL: 'https://dev-api.officemail.io/v1',
      restURL: 'https://dev-api.officemail.io/v1',
      contactURL: 'https://dev-crm-contacts.officemail.io',
      logoApiURL: 'https://api.logo.bi',
      officeNextURL: 'https://stage-api.officenext.net',
      URL: 'https://dev-api.officemail.io/graphql',
      JMAP_URL: 'https://mail.officemail.app/jmap',
      BASE_URL: 'https://mail.officemail.app',
      mailboxApiURL: 'https://mailbox-api.officemail.app/api/v1',
      storageApiURL: 'https://dev-storage.officemail.io/api/v1',
      scheduleApiURL: 'https://dev-schedule-send.officemail.io',
      secureSendApiURL: 'https://dev-secure-send.officemail.io',
      readReceiptApiURL: 'https://dev-read-receipt.officemail.io',
      aiApiURL: 'https://dev-api.officemail.ai',
      openSearchApiURL: 'https://search.officemail.app/api/v1',
      wsURL: null,
      authURL: 'https://auth.officemail.app/api/v1',
      orgChartURL: 'https://orgchart.officemail.app/v1',
      maintenanceURL: 'https://maintenance.officemail.io/dev/notice.json',
    },
    officeNextURL: 'https://stage-org.officenext.net',
  }
}

const { server, officeNextURL } = getServerConfig()

const confProp: ConfProp = {
  DEBUG: __ENABLE_DEBUG__,
  SERVER: server,
  officeNextURL,
}

confProp.defaultLocale = navigator.language || navigator.userLanguage || 'en-US' // 'ko-KR'

if (window.globalConfig != null) {
  if (window.globalConfig.JMAP_URL != null) {
    confProp.SERVER.JMAP_URL = window.globalConfig.JMAP_URL
  }
  if (window.globalConfig.wsURL != null) {
    confProp.SERVER.wsURL = window.globalConfig.wsURL
  }
  if (window.globalConfig.LOCALE != null) {
    confProp.defaultLocale = window.globalConfig.LOCALE
  }
}

confProp.gitInfo = gitInfo

const conf = confProp

export default conf
export type { ServerConfig, ConfProp }
